import { API_BASE_URL } from './index';

// Store token in localStorage
const TOKEN_KEY = 'bankivo_assist_token';
const USER_KEY = 'bankivo_assist_user';

function getCompanyIdFromUser(u) {
  if (!u) return null;
  return u.companyId || u.idcompany || u.company_id || null;
}

export const authApi = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/api/authassist/login`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        usuario: email,  // Backend expects 'usuario' field
        password: password 
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(error.error || 'Invalid email or password');
    }

    
    const data = await response.json();
    
    // Map backend response to frontend format
    const companyId = data.idcompany ?? data.company_id ?? null;
    const user = {
      id: data.id,
      email: data.usuario,
      name: data.nombre,
      whatsapp: data.whatsapp,
      role: data.rol || 'agent',
      companyId,
      companyName: data.nombre_compania,
      sessionToken: data.session_token,
      permissions: data.permisos || [],
    };

    return {
      user,
      token: data.session_token, // Use session_token as token
    };
  },

  logout: async () => {
    const token = getToken();
    const user = getUser();
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/api/authassist/logout`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Token': token,
            'X-Company-ID': getCompanyIdFromUser(user) || '',
          },
          body: JSON.stringify({ 
            session_token: token,
            company_id: getCompanyIdFromUser(user),
          }),
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    return { success: true };
  },

  getCurrentUser: async () => {
    const token = getToken();
    const user = getUser();
    if (!token) {
      throw new Error('No token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/authassist/me`, {
      headers: { 
        'X-Session-Token': token,
        'X-Company-ID': getCompanyIdFromUser(user) || '',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get current user');
    }

    const data = await response.json();
    
    // Map backend response to frontend format
    const companyId = data.idcompany ?? data.company_id ?? null;
    return {
      id: data.id,
      email: data.usuario,
      name: data.nombre,
      role: data.rol || 'agent',
      whatsapp: data.whatsapp,
      companyId,
      companyName: data.nombre_compania,
      sessionToken: data.session_token,
      permissions: data.permisos || [],
    };
  },
};

// Helper functions for token management
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);

/**
 * Reads stored user and ensures `companyId` is set from idcompany / company_id (legacy keys).
 */
export const getUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  const u = JSON.parse(userStr);
  const cid = getCompanyIdFromUser(u);
  if (cid && u.companyId !== cid) {
    const fixed = { ...u, companyId: cid };
    localStorage.setItem(USER_KEY, JSON.stringify(fixed));
    return fixed;
  }
  return u;
};

/** For API headers (X-Company-ID). */
export const getCompanyId = () => getCompanyIdFromUser(getUser());

export const setUser = (user) => localStorage.setItem(USER_KEY, JSON.stringify(user));
